/**
 * Sing4YouLiveStream — Host / Audience viewer
 *
 * B4 FIX — VideoSlot matched track by private _uintid field
 *   OLD: videoTracks.find(t => t._uintid === user.uid || t.getUserId?.() === user.uid)
 *        _uintid is a private agora-rtc-sdk-ng field — not part of the public API.
 *        It can be renamed or removed in any patch release. When it mismatches,
 *        track is undefined, VideoSlot renders nothing, and audience sees blank.
 *   FIX: Access user.videoTrack directly. This is the documented Agora Web SDK
 *        pattern — the SDK attaches the track object onto the remote user once
 *        useRemoteVideoTracks subscribes to it. Pass the full user object into
 *        VideoSlot and call user.videoTrack.play(container).
 *        videoTracks array is still held (required to keep SDK subscriptions alive)
 *        but is no longer searched by private field.
 *
 * ALL OTHER PRODUCTION FIXES UNCHANGED:
 *   A. CONNECTION RECOVERY — connection-state-change listener
 *   B. TOKEN RENEWAL      — token-privilege-will/did-expire handlers
 *   C. AUDIO RECOVERY     — re-plays on audioTracks change
 *   D. LOADING STATE      — spinner while joining
 *   PVLC overlay          — shows pvlc_load/pvlc_sync badge + progress bar
 *   Karaoke overlay       — animated EQ bars + floating notes
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Typography,
} from "@mui/material";
import { CallEnd, Fullscreen, FullscreenExit, MicOff, MovieFilter, VideocamOff } from "@mui/icons-material";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import {
  useJoin,
  useRTCClient,
  useRemoteAudioTracks,
  useRemoteUsers,
  useRemoteVideoTracks,
} from "agora-rtc-react";
import { AGORA_APP_ID } from "../../../constants/config";
import { getAgoraToken } from "../../../services/api/agora.api";
import {
  RemoteStreamFallbackType,
  RemoteStreamType,
} from "agora-rtc-sdk-ng";
import "../../../styles/sing4you-live.css";

/* ─── Karaoke overlay ───────────────────────────────────────────────────── */
const KQ = ["#ff6b9d", "#22d3ee", "#a855f7", "#fbbf24"] as const;
const KQ_BARS = [
  { h: 22, dur: "0.38s", delay: "0.00s" }, { h: 14, dur: "0.45s", delay: "0.07s" },
  { h: 28, dur: "0.33s", delay: "0.14s" }, { h: 10, dur: "0.52s", delay: "0.03s" },
  { h: 24, dur: "0.41s", delay: "0.21s" }, { h: 18, dur: "0.35s", delay: "0.10s" },
  { h: 30, dur: "0.49s", delay: "0.28s" }, { h: 12, dur: "0.43s", delay: "0.05s" },
];
const KQ_NOTES = [
  { n: "♪", left: "8%",  dur: "4.2s", delay: "0.0s",  size: "1.5rem", col: "#ff6b9d" },
  { n: "♫", left: "22%", dur: "5.1s", delay: "0.8s",  size: "1.8rem", col: "#22d3ee" },
  { n: "♬", left: "38%", dur: "3.9s", delay: "1.6s",  size: "1.3rem", col: "#a855f7" },
  { n: "♩", left: "54%", dur: "4.7s", delay: "0.4s",  size: "1.6rem", col: "#fbbf24" },
];

function KaraokeOverlay({ trackName }: { trackName: string }) {
  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", borderRadius: "inherit", zIndex: 8 }}>
      {KQ_NOTES.map((n, i) => (
        <Box key={i} sx={{
          position: "absolute", left: n.left, bottom: "18%",
          color: n.col, fontSize: n.size, textShadow: `0 0 12px ${n.col}`,
          animation: `floatNote ${n.dur} ${n.delay} ease-in infinite`,
          "@keyframes floatNote": { "0%": { opacity: 0, transform: "translateY(0) scale(0.8)" }, "15%": { opacity: 0.9, transform: "translateY(-40px) scale(1)" }, "100%": { opacity: 0, transform: "translateY(-250px) scale(0.7)" } },
        }}>
          {n.n}
        </Box>
      ))}
      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, pt: 5, pb: 1.5, px: 2, background: "linear-gradient(to top, rgba(0,0,0,0.95) 0%, transparent 100%)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Typography sx={{ color: "#ff6b9d", fontWeight: 900, fontSize: "0.85rem", letterSpacing: 2 }}>KARAOKE LIVE</Typography>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "3px", height: 24 }}>
            {KQ_BARS.map((b, i) => (
              <Box key={i} sx={{ width: 4, bgcolor: KQ[i % 4], height: b.h, animation: `eqBar ${b.dur} ${b.delay} infinite alternate`, transformOrigin: "bottom", "@keyframes eqBar": { from: { transform: "scaleY(0.3)" }, to: { transform: "scaleY(1)" } } }} />
            ))}
          </Box>
        </Box>
        {trackName && <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem" }}>♪ {trackName}</Typography>}
      </Box>
    </Box>
  );
}

/* ─── PVLC overlay ──────────────────────────────────────────────────────── */
function PVLCOverlay({ title, time, playing, duration }: { title: string; time: number; playing: boolean; duration: number }) {
  const pct = duration > 0 ? (time / duration) * 100 : 0;
  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 8 }}>
      <Box sx={{ position: "absolute", top: 16, left: 16, display: "flex", alignItems: "center", gap: 1, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", px: 1.5, py: 0.6, borderRadius: "6px", border: "1px solid rgba(255,107,157,0.4)" }}>
        <MovieFilter sx={{ color: "#ff6b9d", fontSize: "0.9rem" }} />
        <Typography sx={{ color: "#ff6b9d", fontWeight: 800, fontSize: "0.75rem", letterSpacing: 1.5 }}>PVLC LIVE</Typography>
        {!playing && <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", ml: 0.5 }}>— PAUSED</Typography>}
      </Box>
      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, pt: 4, pb: 1.5, px: 2, background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)" }}>
        {title && <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem", mb: 0.8 }}>🎬 {title}</Typography>}
        {duration > 0 && (
          <Box sx={{ position: "relative", height: 3, bgcolor: "rgba(255,255,255,0.15)", borderRadius: 2 }}>
            <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, bgcolor: "#ff6b9d", borderRadius: 2, transition: "width 1s linear" }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ─── VideoSlot ─────────────────────────────────────────────────────────────
 * B4 FIX: uses user.videoTrack (documented Agora pattern) instead of the
 * private _uintid field. The track is attached to the user object by
 * useRemoteVideoTracks — no manual lookup needed.
 *
 * Imperative play() is still used (vs <RemoteUser>) because it:
 *   • Re-runs whenever the track reference changes (camera toggle / reconnect)
 *   • Gives Agora a live DOM target which keeps the subscription alive
 *   • Matches the Agora Web SDK docs recommended production pattern
 * ─────────────────────────────────────────────────────────────────────────── */
function VideoSlot({ user }: { user: any }) {
  const containerRef = useRef<HTMLDivElement>(null);

  // user.videoTrack is populated by useRemoteVideoTracks() in the parent.
  // It is undefined when the talent's camera is off, and a real track object
  // when it is on. The track reference itself is the signal — not user.hasVideo.
  const track = user.videoTrack;

  useEffect(() => {
    const el = containerRef.current;

    // Camera is off or container not yet mounted — nothing to play
    if (!el || !track) return;

    // play() injects a <video> element into the container div and starts
    // decoding frames. Called every time the track reference changes, so
    // camera toggle (off → on) always gets a fresh play() call.
    try {
      track.play(el);
    } catch (err) {
      console.warn("[VideoSlot] track.play() failed:", err);
    }

    return () => {
      // stop() detaches the <video> element cleanly when the track goes away
      // (camera turned off) or the component unmounts.
      try { track.stop(); } catch {}
    };
    // IMPORTANT: depend only on `track`.
    // user.hasVideo and user.hasAudio look tempting but the SDK mutates them
    // in-place on the same object reference — React does not see the change
    // as a new render trigger, so adding them to deps has no effect and adds
    // confusion. The track reference changing from undefined ↔ IRemoteVideoTrack
    // is the only reliable signal for camera on/off.
  }, [track]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: "#0c1128" }}>
      {/* Agora injects a <video> element here via track.play(el) */}
      <div ref={containerRef} style={{ width: "100%", height: "100%", objectFit: "cover" as any }} />

      {/* Camera-off overlay — shown when track is absent */}
      {!track && (
        <div className="s4y-remote-cam-off">
          <div className="s4y-remote-cam-off-icon"><VideocamOff sx={{ fontSize: "2.2rem" }} /></div>
          <span className="s4y-remote-cam-off-title">Camera is off</span>
          <span className="s4y-remote-cam-off-sub">The host has turned off their camera</span>
        </div>
      )}

      {!user.hasAudio && (
        <div className="s4y-remote-mic-badge" title="Microphone is muted">
          <MicOff sx={{ fontSize: "0.85rem" }} />
        </div>
      )}
    </div>
  );
}

/* ─── Multi-stream grid ─────────────────────────────────────────────────── */
function MultiStreamGrid({ remoteUsers, loading }: { remoteUsers: any[]; loading: boolean }) {
  const count    = Math.min(remoteUsers.length, 4);
  const isSingle = count === 1;
  return (
    <div
      className={`s4y-stream-grid${isSingle ? " s4y-stream-grid--single" : ""}`}
      data-count={count || 1}
      style={{ position: "absolute", inset: 0 }}
    >
      {loading && remoteUsers.length === 0 && (
        <div className="s4y-video-wait">
          <CircularProgress size={36} sx={{ color: "#ff6b9d", mb: 2 }} />
          <span className="s4y-video-wait-title">Connecting…</span>
          <span className="s4y-video-wait-sub">Joining the live session</span>
        </div>
      )}
      {!loading && remoteUsers.length === 0 && (
        <div className="s4y-video-wait">
          <div className="s4y-video-wait-pulse">🎤</div>
          <span className="s4y-video-wait-title">Waiting for the host…</span>
          <span className="s4y-video-wait-sub">The live session hasn't started yet</span>
        </div>
      )}
      {/* B4 FIX: VideoSlot receives user object; it reads user.videoTrack internally */}
      {remoteUsers.map((user) => (
        <VideoSlot key={user.uid} user={user} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function Sing4YouLiveStream({
  userId,
  videoId,
  displayName,
  onCallEnd,
  onNetworkError,
  qrCodeClickHandler,
  karaokeActive    = false,
  karaokeTrackName = "",
  pvlcActive   = false,
  pvlcTitle    = "",
  pvlcTime     = 0,
  pvlcPlaying  = false,
  pvlcDuration = 0,
  isFullscreen:     isFullscreenProp,
  onToggleFullscreen,
}: {
  userId: string;
  videoId: string;
  displayName?: string;
  onCallEnd: () => void;
  onNetworkError?: () => void;
  qrCodeClickHandler?: () => void;
  karaokeActive?: boolean;
  karaokeTrackName?: string;
  pvlcActive?: boolean;
  pvlcTitle?: string;
  pvlcTime?: number;
  pvlcPlaying?: boolean;
  pvlcDuration?: number;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}) {
  const [token,             setToken]             = useState<string | null>(null);
  const [isFullscreenLocal, setIsFullscreenLocal] = useState(false);
  const [showLeaveDialog,   setShowLeaveDialog]   = useState(false);
  const [loading,           setLoading]           = useState(true);

  const fullscreenRef       = useRef<HTMLDivElement>(null);
  const hasEverConnectedRef = useRef(false);
  const agoraClient         = useRTCClient();

  const parentControlled = onToggleFullscreen !== undefined;
  const isFullscreen     = parentControlled ? (isFullscreenProp ?? false) : isFullscreenLocal;
  const toggleFullscreen = parentControlled
    ? onToggleFullscreen
    : () => {
        if (isFullscreenLocal) document.exitFullscreen().catch(() => {});
        else fullscreenRef.current?.requestFullscreen().catch(() => {});
      };

  /* ── Token fetch ─────────────────────────────────────────────────────── */
  const fetchAndSetToken = useCallback(async () => {
    try {
      const t = await getAgoraToken(videoId, "0");
      setToken(t);
      return t;
    } catch (err) {
      console.error("[Sing4YouLiveStream] Token fetch failed:", err);
      return null;
    }
  }, [videoId]);

  useEffect(() => {
    fetchAndSetToken().then((t) => {
      if (!t) {
        setLoading(false);
        enqueueSnackbar("Could not connect to stream. Try again later.", { variant: "error", autoHideDuration: 5000 });
      }
    });
  }, [fetchAndSetToken, enqueueSnackbar]);

  /* ── Agora RTC hooks ─────────────────────────────────────────────────── */
  useJoin({ appid: AGORA_APP_ID!, channel: videoId, token: token!, uid: 0 }, !!token);

  const remoteUsers = useRemoteUsers();

  // Must hold videoTracks reference — SDK uses it to keep subscriptions alive.
  // B4 FIX: the array itself is held but NOT searched by _uintid.
  // Each user.videoTrack is read directly inside VideoSlot.
  const { videoTracks } = useRemoteVideoTracks(remoteUsers); // eslint-disable-line @typescript-eslint/no-unused-vars

  const { audioTracks } = useRemoteAudioTracks(remoteUsers);
  useEffect(() => {
    audioTracks.forEach((t) => { try { t.play(); } catch {} });
  }, [audioTracks]);

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => setLoading(false), 3000);
    return () => clearTimeout(timer);
  }, [token]);

  /* ── Dual-stream viewer: default high; per-host fallback to low on weak downlink ─ */
  useEffect(() => {
    if (!agoraClient) return;
    agoraClient.setRemoteDefaultVideoStreamType(RemoteStreamType.HIGH_STREAM).catch(() => {});
  }, [agoraClient]);

  useEffect(() => {
    if (!agoraClient || remoteUsers.length === 0) return;
    remoteUsers.forEach((u: { uid: string | number }) => {
      agoraClient.setStreamFallbackOption(u.uid, RemoteStreamFallbackType.LOW_STREAM).catch(() => {});
    });
  }, [agoraClient, remoteUsers]);

  /* ── Connection state recovery ───────────────────────────────────────── */
  useEffect(() => {
    if (!agoraClient) return;
    const handle = (curState: string, _prev: string, reason?: string) => {
      if (curState === "RECONNECTING") {
        enqueueSnackbar("⏳ Reconnecting to stream…", { variant: "warning", persist: true, key: "s4y-reconnecting" });
      }
      if (curState === "CONNECTED") {
        hasEverConnectedRef.current = true;
        closeSnackbar("s4y-reconnecting");
        audioTracks.forEach((t) => { try { t.play(); } catch {} });
      }
      if (curState === "DISCONNECTED") {
        if (reason === "NETWORK_ERROR") {
          closeSnackbar("s4y-reconnecting");
          enqueueSnackbar("❌ Connection lost — rejoining…", { variant: "error", autoHideDuration: 4000 });
          onNetworkError?.();
        } else if (!hasEverConnectedRef.current) {
          setLoading(false);
          enqueueSnackbar("Unable to connect to the stream. Check your network or try again.", { variant: "error", autoHideDuration: 6000 });
        }
      }
    };
    agoraClient.on("connection-state-change", handle);
    return () => { agoraClient.off("connection-state-change", handle); closeSnackbar("s4y-reconnecting"); };
  }, [agoraClient, audioTracks, onNetworkError]);

  /* ── Token expiry ────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!agoraClient) return;
    const onWillExpire = async () => {
      const t = await fetchAndSetToken();
      if (t) { try { await agoraClient.renewToken(t); } catch {} }
    };
    const onDidExpire = async () => {
      enqueueSnackbar("🔄 Refreshing session…", { variant: "info", autoHideDuration: 3000 });
      const t = await fetchAndSetToken();
      if (t) { try { await agoraClient.renewToken(t); } catch { onNetworkError?.(); } }
    };
    agoraClient.on("token-privilege-will-expire", onWillExpire);
    agoraClient.on("token-privilege-did-expire",  onDidExpire);
    return () => {
      agoraClient.off("token-privilege-will-expire", onWillExpire);
      agoraClient.off("token-privilege-did-expire",  onDidExpire);
    };
  }, [agoraClient, fetchAndSetToken, onNetworkError]);

  /* ── Fullscreen detection ────────────────────────────────────────────── */
  useEffect(() => {
    if (parentControlled) return;
    const handler = () => setIsFullscreenLocal(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [parentControlled]);

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div
      ref={fullscreenRef}
      style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden", background: "#0c1128" }}
    >
      {/* B4 FIX: no videoTracks prop — VideoSlot reads user.videoTrack directly */}
      <MultiStreamGrid remoteUsers={remoteUsers} loading={loading} />

      {karaokeActive && <KaraokeOverlay trackName={karaokeTrackName} />}
      {pvlcActive && (
        <PVLCOverlay title={pvlcTitle} time={pvlcTime} playing={pvlcPlaying} duration={pvlcDuration} />
      )}

      <div className="s4y-controls-bar">
        <button className="s4y-ctrl-btn" onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
          {isFullscreen ? <FullscreenExit sx={{ fontSize: "1.1rem" }} /> : <Fullscreen sx={{ fontSize: "1.1rem" }} />}
        </button>
        <span className="s4y-ctrl-divider" />
        <button className="s4y-ctrl-btn end" onClick={() => setShowLeaveDialog(true)} title="Leave session">
          <CallEnd sx={{ fontSize: "1.1rem" }} />
        </button>
      </div>

      <Dialog open={showLeaveDialog} onClose={() => setShowLeaveDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: "#0f1225", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2, color: "#fff" } }}>
        <DialogTitle sx={{ color: "#fff" }}>Leave Session?</DialogTitle>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setShowLeaveDialog(false)} variant="outlined" size="small" sx={{ borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" }}>Stay</Button>
          <Button onClick={onCallEnd} variant="contained" color="error" size="small">Leave</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}